// Renders parsed wiki markdown to React elements directly - no HTML string,
// no dangerouslySetInnerHTML, anywhere in this path. See markdown.ts's module
// doc for why that is the actual safety property this feature relies on.
import { Fragment, type CSSProperties, type ReactNode } from 'react';
import { Link } from '../design/router';
import { parseMarkdown, type BlockNode, type InlineNode, type TableAlign } from './markdown';

function renderInline(nodes: InlineNode[], keyPrefix: string): ReactNode {
  return nodes.map((node, i) => {
    const key = `${keyPrefix}-${i}`;
    switch (node.type) {
      case 'text':
        return <Fragment key={key}>{node.value}</Fragment>;
      case 'strong':
        return <strong key={key}>{renderInline(node.children, key)}</strong>;
      case 'em':
        return <em key={key}>{renderInline(node.children, key)}</em>;
      case 'code':
        return (
          <code key={key} style={{ fontFamily: 'var(--ss-mono)', background: 'var(--ss-panel)', padding: '2px 5px', borderRadius: 4, fontSize: '0.92em' }}>
            {node.value}
          </code>
        );
      case 'link':
        return node.href.startsWith('/') ? (
          <Link key={key} to={node.href} style={{ color: 'var(--ss-accent)' }}>
            {renderInline(node.children, key)}
          </Link>
        ) : (
          <a key={key} href={node.href} target="_blank" rel="noreferrer" style={{ color: 'var(--ss-accent)' }}>
            {renderInline(node.children, key)}
          </a>
        );
      case 'image':
        return (
          <img
            key={key}
            src={node.src}
            alt={node.alt}
            loading="lazy"
            style={{ maxWidth: '100%', borderRadius: 8, display: 'block', margin: '10px 0' }}
          />
        );
      default:
        return null;
    }
  });
}

function renderBlock(block: BlockNode, key: string): ReactNode {
  switch (block.type) {
    case 'heading': {
      const Tag = `h${block.level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      return (
        <Tag key={key} id={block.id}>
          {renderInline(block.children, key)}
        </Tag>
      );
    }
    case 'paragraph':
      return (
        <p key={key} style={{ lineHeight: 1.7, color: 'var(--ss-text-2)' }}>
          {renderInline(block.children, key)}
        </p>
      );
    case 'blockquote':
      return (
        <blockquote
          key={key}
          style={{ borderLeft: '3px solid var(--ss-border)', margin: '12px 0', padding: '2px 16px', color: 'var(--ss-text-3)' }}
        >
          {block.children.map((child, i) => renderBlock(child, `${key}-${i}`))}
        </blockquote>
      );
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag key={key} style={{ lineHeight: 1.7, color: 'var(--ss-text-2)', paddingLeft: 24 }}>
          {block.items.map((item, i) => (
            <li key={`${key}-${i}`}>{renderInline(item, `${key}-${i}`)}</li>
          ))}
        </Tag>
      );
    }
    case 'code_block':
      return (
        <pre
          key={key}
          style={{ background: 'var(--ss-panel)', border: '1px solid var(--ss-border-soft)', borderRadius: 10, padding: '12px 14px', overflowX: 'auto' }}
        >
          <code style={{ fontFamily: 'var(--ss-mono)', fontSize: 12.5 }}>{block.value}</code>
        </pre>
      );
    case 'hr':
      return <hr key={key} style={{ border: 'none', borderTop: '1px solid var(--ss-border-soft)', margin: '20px 0' }} />;
    case 'table': {
      const cellStyle = (align: TableAlign | undefined): CSSProperties => ({
        textAlign: align ?? 'left',
        padding: '7px 12px',
        borderBottom: '1px solid var(--ss-border-soft)',
      });
      return (
        <div key={key} style={{ overflowX: 'auto', margin: '10px 0' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13.5, color: 'var(--ss-text-2)' }}>
            <thead>
              <tr>
                {block.header.map((cell, i) => (
                  <th key={`${key}-h${i}`} style={{ ...cellStyle(block.align[i]), fontWeight: 600, color: 'var(--ss-text)', borderBottom: '2px solid var(--ss-border)' }}>
                    {renderInline(cell, `${key}-h${i}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={`${key}-r${r}`}>
                  {row.map((cell, c) => (
                    <td key={`${key}-r${r}-c${c}`} style={cellStyle(block.align[c])}>
                      {renderInline(cell, `${key}-r${r}-c${c}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'toc':
      if (block.items.length === 0) return null;
      return (
        <nav key={key} aria-label="Table of contents" style={{ border: '1px solid var(--ss-border-soft)', borderRadius: 10, padding: '14px 18px', margin: '14px 0' }}>
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ss-text-3)' }}>Contents</span>
          <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {block.items.map((item, i) => (
              <li key={`${key}-${i}`} style={{ paddingLeft: (item.level - 1) * 14 }}>
                <a href={`#${item.id}`} style={{ fontSize: 13, color: 'var(--ss-accent)' }}>
                  {item.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      );
    default:
      return null;
  }
}

export function MarkdownView({ source }: { source: string }) {
  const blocks = parseMarkdown(source);
  if (blocks.length === 0) {
    return <p style={{ color: 'var(--ss-text-3)', fontSize: 13.5 }}>This page is empty.</p>;
  }
  return <div className="wiki-markdown">{blocks.map((b, i) => renderBlock(b, `b${i}`))}</div>;
}
